
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../i18n";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import { Schemes, Utils } from "../../../../utils";
import * as ReactRouterDom from "react-router-dom";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { Alignment, Button, ButtonManageRef, Checkbox, Classname, Column, DateTimeInput, DateTimePickerLayout, DateTimePickerSelectionMode, Dropdown, FormControl, StringHelper, Table, useScreenDimension, useSubscription } from "@rivtn/noseur";
import { ApiResponseData, BaseService, IBillingHistory, IMember, IPaymentMethod, IPlatformPlan, MemberService, PlatformService, SanitizedResponse } from "../../../../services";
import StatefulLink from "../../../shared/StatefulLink";
import { CardForm } from "@ploutous/react-library";
import { Elements } from "../../../shared/Elements";
import { ChangePlan } from "./ManagePlan";

export interface BillingProps {
    profile: IMember;
}

function Billing(props: BillingProps) {

    let { profile } = props;
    const { width } = useScreenDimension();
    const memberService = MemberService.getInstance();
    const platformService = PlatformService.getInstance();
    const activeDialogMutableRoot = React.useRef<ReactDOM.Root>(null);
    const updateProfile = useSubscription("OMNEHOST.PROFILE.UPDATED");
    const labels = i18nManager.Labels.account.plan_and_billing.billing;
    const [platformPlans, setPlatformPlans] = React.useState<IPlatformPlan[]>();
    const [paymentMethods, setPaymentMethods] = React.useState<IPaymentMethod[]>();
    const [nextUpgradeAblePlan, setNextUpgradeAblePlan] = React.useState<IPlatformPlan>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [billingHistories, setBillingHistories] = React.useState<ApiResponseData<IBillingHistory>>({});

    React.useEffect(() => {
        fetchPlatformPlans();
        fetchPaymentMethods();
        fetchBillingHistory();
    }, []);

    return (<div className="account-setting">
        <div className="header">
            <span className="title">{i18nManager.Labels.account.plan_and_billing.title} - {labels.title}</span>
        </div>
        <div className="billing">
            {nextUpgradeAblePlan && (<div className="upgrade-banner" style={{
                "--planBorderColor": `rgb(${nextUpgradeAblePlan.rgb_csv})`,
                "--planBackgroundColor": `rgba(${nextUpgradeAblePlan.rgb_csv}, 0.1)`
            } as any}>
                <i className="fa fa-user" />
                <div className="details">
                    <span className="t">{nextUpgradeAblePlan.name}</span>
                    <span className="d">{nextUpgradeAblePlan.reason_to_upgrade?.[i18nManager.ActiveLanguage]}</span>
                    <Button scheme={Schemes.OMNEHOST} text={labels.upgrade_to + " " + nextUpgradeAblePlan.name} onClick={() => ChangePlan.to({
                        profile,
                        paymentMethods,
                        onClose: () => { },
                        activeDialogMutableRoot,
                        updateProfile: (_profile) => {
                            profile = _profile;
                            updateProfile(profile);
                        },
                        onSuccess: () => {
                            fetchPlatformPlans();
                        },
                    }, "UPGRADE", nextUpgradeAblePlan, nextUpgradeAblePlan.__calculated_amounts?.[(profile.extra_data?.current_plan_duration as any ?? "MONTHLY").toLowerCase() as ("yearly" | "monthly")].amount, "info")} />
                </div>
            </div>)}
            <div className="auto-renew">
                <div className="control">
                    <Checkbox checked={profile.extra_data.auto_renew_plan} scheme={Schemes.OMNEHOST} onChange={(e: any) => toggleAutoRenewal(e.checked)}
                        label={<span className="auto-renew-ctrl-l" style={{ color: "var(--textColor)" }}>{labels.auto_renew}</span>} alignLabel={Alignment.LEFT} />
                </div>
                <span className="d">{labels.auto_renew_desc}</span>
            </div>
            <div className="payment-methods">
                <span className="t">{labels.payment_methods}</span>
                <div className="methods">
                    {!paymentMethods?.length && (<div style={{ display: "flex", alignItems: "center" }}>
                        <img alt={"loading plans and features"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
                    </div>)}
                    {paymentMethods?.map(buildPaymentMethod)}
                    <div className="add" onClick={addNewCard}>
                        <i className="fa fa-plus" />
                    </div>
                </div>
            </div>
            <div className="billing-history">
                <div className="header">
                    <div style={{ fontSize: 18, fontWeight: "bold", marginRight: 10 }}>
                        {labels.billing_history}
                    </div>
                    <div className="control">
                        <StatefulLink label={labels.download_history} alignLoader="LEFT" style={width < 800 ? { marginTop: 10 } : undefined} onClick={(done) => {
                            downloadBillingHistory();
                            done?.();
                        }} />
                        <Dropdown fill scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.plan, }}
                            selectedOptionIndex={(platformPlans?.findIndex((p) => p?.code === (searchParams.get("biplan"))) ?? -1) + 1}
                            options={[{ code: "", name: i18nManager.Labels.common.all }, ...(platformPlans ?? [])]} optionMap={{
                                value: "code",
                                label: "name",
                            }} onSelectOption={(option) => {
                                Utils.updateSearchParams("biplan", option.code, setSearchParams, fetchBillingHistory);
                            }} />
                        {(width > 800) && (<FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={labels.date_range} rightContent={"fa fa-calendar"}>
                            <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT}
                                selectedDates={[new Date(searchParams.get("bifrom") ?? "2025-02-01 00:00:00 UTC"), new Date(searchParams.get("bito") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                                selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                                    Utils.updateSearchParamses({
                                        "bito": Utils.formatToApiDate(options.toDate!, false, false),
                                        "bifrom": Utils.formatToApiDate(options.fromDate!, false, false),
                                    }, setSearchParams, fetchBillingHistory);
                                }} />
                        </FormControl>)}
                    </div>
                </div>
                <Table scheme={Schemes.OMNEHOST} data={billingHistories.content} totalRecords={billingHistories.total_elements} dataRefreshKeys={["id", "created_at"]}
                    rowsPerPage={200} loadingState={Elements.LOADING_STATE}>
                    <Column className="table-c" header={labels.table.headers.date} dataKey="created_at" template={(v) => Utils.formatToFineDate(v, true)} />
                    <Column className="table-c" header={labels.table.headers.details} template={(v: IBillingHistory) => {
                        return (<span><span style={{
                            fontWeight: "bold",
                            color: (v.type === "UPGRADE" ? "#0FA883" : "#FF7171"),
                        }}>{Utils.toSaneSentenceFormat(v.type)}</span> {labels.table.to} {Utils.toSaneSentenceFormat(v.plan)} {labels.table.duration[v.duration]}</span>);
                    }} />
                    <Column className="table-c" header={labels.table.headers.amount} template={(v: IBillingHistory) => `${StringHelper.toPresentableMoneyValue(v.amount)} ${v.currency}`} />
                    <Column className="table-c" doNotRender={width < 800} header={labels.table.headers.payment_method} dataKey="payment_method" template={(v) => {
                        if (!!v.masked_card_number) return `${labels.payment_method_label.card} - ${Utils.addSeparatorAtEveryDivisor(v.masked_card_number, 4)}`;
                        if (v) return `${labels.payment_method_label.payment_link}`;
                        return;
                    }} />
                    <Column className="table-c" doNotRender={width < 800} header={labels.table.headers.receipt} dataKey="receipt_download_location" template={(v) => {
                        if (!v) return null;
                        const vs = v.split("/");
                        return (<a href={v} target="_blank" rel="noreferrer">{vs[vs.length - 1]}</a>);
                    }} />
                </Table>
            </div>
        </div>
    </div>);

    function fetchPlatformPlans() {
        platformService.queryPlans(profile.extra_data?.currency_code ?? "NOT_SET").then(({ apiResponse }: SanitizedResponse<IPlatformPlan>) => {
            setPlatformPlans(apiResponse.data!.content);
            const currentPlanIndex = apiResponse.data!.content?.findIndex((p) => p.code === profile.plan) ?? 0;
            if (currentPlanIndex < (apiResponse.data!.content?.length! - 1)) {
                const nextPlan = apiResponse.data!.content?.[currentPlanIndex + 1];
                if (nextPlan) {
                    nextPlan.rgb_csv = nextPlan?.rgb_csv?.split(",")?.map((c) => c.trim())?.join(",") ?? "71,159,200";
                    setNextUpgradeAblePlan(nextPlan);
                }
            } else {
                setNextUpgradeAblePlan(undefined);
            }
        }).catch(BaseService.reportError);
    }

    function fetchPaymentMethods() {
        memberService.queryPaymentMethods(profile.extra_data.currency_code).then(({ apiResponse }: SanitizedResponse<IPaymentMethod>) => {
            setPaymentMethods(apiResponse.data?.content);
        }).catch(BaseService.reportError);
    }

    function fetchBillingHistory() {
        setBillingHistories({});
        memberService.queryBillingHistories(profile.extra_data.currency_code, {
            page: 1,
            size: 200,
            status: "SUCCESSFUL",
            plan: searchParams.get("biplan") ?? "",
            created_at_from: searchParams.get("bifrom") ?? "2025-02-01 01:00:00 GMT",
            created_at_to: searchParams.get("bito") ?? Utils.formatToApiDate(new Date()),
        }).then(({ apiResponse }: SanitizedResponse<IBillingHistory>) => {
            setBillingHistories(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function buildPaymentMethod(paymentMethod: IPaymentMethod) {
        const type = paymentMethod.type?.toLowerCase();
        const { details, is_default: isDefault } = paymentMethod;
        let typeLabel = (labels.payment_method_label as any)[type] as any;
        const brand = (paymentMethod.details?.card_brand ?? type).toLowerCase();

        return (<div key={paymentMethod.external_id ?? type} className={Classname.build({ card: type === "card", "active": isDefault })} onClick={() => {
            memberService.changeDefaultPaymentMethod(paymentMethod.external_id).then(() => {
                Siren.alert(labels.default_payment_method_updated);
                fetchPaymentMethods();
            }).catch(BaseService.reportError);
        }}>
            {isDefault && (<i className="fa fa-check" />)}
            <span className="t">{typeLabel}</span>
            <div className="content">
                {type === "payment_link" && (<span>{labels.receive_renew_payment_link}</span>)}
                {type === "card" && (<>
                    <span>{Utils.addSeparatorAtEveryDivisor(details.masked_card_number, 4)}</span>
                    <span className="lined-limit" style={{ "--maxTextLine": 2 } as any}>{details.name}</span>
                </>)}
            </div>
            <img alt={type} src={Utils.paymentMethodLogoMap(brand)} className="method-logo" />
            {type !== "payment_link" && (<StatefulLink alignLoader="LEFT" className="delete" label={<i className="fa fa-trash" style={{ fontSize: 20, color: "#FF7171" }} />} onClick={(done, e) => {
                e.stopPropagation();
                memberService.deletePaymentMethod(paymentMethod.external_id).then(() => {
                    Siren.alert(labels.payment_method_deleted);
                    fetchPaymentMethods();
                }).catch(BaseService.reportError).finally(done);
            }} />)}
        </div>);
    }

    function toggleAutoRenewal(checked: boolean) {
        memberService.upsertProfile({ ...profile, auto_renew_plan: checked }).then(({ sanitized }: SanitizedResponse<IMember>) => {
            Siren.alert(labels.auto_renew_updated);
            updateProfile(sanitized);
        }).catch(BaseService.reportError);
    }

    function addNewCard() {
        const cardFormRef = { current: null } as any;
        const dialogErrorRef = { current: null } as any;
        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: "90%" },
            title: labels.dialog.add_new_card.title,
            content: (<div style={{ margin: "10px 10px", marginBottom: 30 }}>
                <CardForm manageRef={cardFormRef} container={{ style: { width: 400 } }}
                    cardHolderName={`${profile.user_detail?.first_name ?? ""} ${profile.user_detail?.last_name ?? ""}`}
                    style={{
                        cvvInput: {
                            marginLeft: "10px",
                        },
                        expiryCvvContainer: {
                            margin: "10px 0px",
                        },
                        highlightColors: {
                            success: "#479FC8"
                        },
                    }}
                    onMessage={(key, type, message) => {
                        if (type !== "error") {
                            dialogErrorRef.current!.innerText = " ";
                            return;
                        }
                        dialogErrorRef.current!.innerText = message;
                    }} />
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.current!.innerText = " ";
                try {
                    const tokenization = await cardFormRef.current?.tokenize();
                    await memberService.addPaymentMethod({
                        type: "CARD",
                        nonce: tokenization.data.nonce,
                    });
                    dialog.hide();
                    fetchPaymentMethods();
                } catch (err: any) {
                    dialogErrorRef.current!.innerText = err.message;
                }
                button?.setLoadingState(false);
                return false;
            }
        });
        dialog.show();
    }

    function downloadBillingHistory() {
        const rows = [[labels.table.headers.date, labels.table.headers.details, labels.table.headers.amount, labels.table.headers.payment_method, labels.table.headers.receipt]]
            .concat((billingHistories?.content ?? []).map((v) => [
                Utils.formatToFineDate(v.created_at, true),
                `${Utils.toSaneSentenceFormat(v.type)} ${labels.table.to} ${Utils.toSaneSentenceFormat(v.plan)} ${labels.table.duration[v.duration]}`,
                `${v.amount / 1000000} ${v.currency}`,
                (!!v.payment_method?.masked_card_number ? `${labels.payment_method_label.card} - ${Utils.addSeparatorAtEveryDivisor(v.payment_method?.masked_card_number, 4)}` : labels.payment_method_label.payment_link),
                `${v.receipt_download_location}`
            ]));
        Utils.downloadBlob(rows.map(row => row.map(String)
            .map((v: any) => v.replaceAll('"', '""'))
            .map((v: any) => `"${v}"`)
            .join(",")
        ).join('\r\n'), `billing_history__${searchParams.get("bifrom") ?? "2025-02-01 01:00:00 GMT"}_${searchParams.get("bito") ?? Utils.formatToApiDate(new Date())}`);
    }

}

export default Billing;
